import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { EMAIL_REGEX } from "@memento/library/email";

import { Button } from "@memento/ui/Button";
import { LabeledInput } from "@memento/ui/Form/LabeledInput";
import { useAuth } from "src/hooks/useAuth";

type ResetPasswordFormValues = {
  email: string;
};

const schema = z.object({
  email: z
    .string()
    .regex(EMAIL_REGEX, { message: "Please provide a valid email address." })
    .max(1000)
    .nonempty({ message: "Please enter your email address." }),
});

type ResetPasswordFormProps = {
  onReset: (email: string) => Promise<void>;
};

export function ResetPasswordForm({ onReset }: ResetPasswordFormProps) {
  const { user, isGuest } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: user?.email && !isGuest ? user.email : "",
    },
  });

  const onSubmit: SubmitHandler<ResetPasswordFormValues> = async ({
    email,
  }) => {
    await onReset(email);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mx-auto max-w-xl space-y-3"
    >
      <LabeledInput
        label="Email"
        id="email"
        type="email"
        error={errors.email?.message}
        {...register("email")}
      />
      <Button
        variant="primary"
        disabled={isSubmitting}
        type="submit"
        className="my-2 pl-2"
      >
        Reset Password
      </Button>
    </form>
  );
}
